import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Navbar.css';

const Navbar = ({ isLoggedIn, onLogout, user, isAdmin }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Consider "scrolled down" when user has scrolled more than 100px
      const isScrolled = window.scrollY > 100;
      setScrolled(isScrolled);
    };

    // Add event listener
    window.addEventListener('scroll', handleScroll);
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleAuthClick = () => {
    if (isLoggedIn) {
      setUserDropdownOpen(!userDropdownOpen);
      setAdminMenuOpen(false); // Close admin menu when opening user dropdown
    } else {
      navigate('/login');
    }
  };

  const handleAdminMenuClick = () => {
    setAdminMenuOpen(!adminMenuOpen);
    setUserDropdownOpen(false); // Close user dropdown when opening admin menu
  };

  const handleLogout = () => {
    onLogout();
    setUserDropdownOpen(false);
    setAdminMenuOpen(false);
  };

  const navigateToSettings = () => {
    navigate('/settings');
    setUserDropdownOpen(false);
  };

  const navigateToAccountSettings = () => {
    navigate('/account-settings');
    setUserDropdownOpen(false);
  };

  const navigateToAdminPage = (path) => {
    navigate(path);
    setAdminMenuOpen(false);
    setMobileMenuOpen(false); // סגירת תפריט מובייל בעת ניווט
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setUserDropdownOpen(false);
    setAdminMenuOpen(false);
  };

  const handleWhatsAppClick = () => {
    // Replace with your WhatsApp number and message
    const phoneNumber = "972502726865"; // WhatsApp number with country code
    const message = "Hello! I'm interested in your services.";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-logo">
          <Link to="/" className="app-name">
            <img src="/images/logo-new.png" alt="ELEF89 Logo" className="logo-image" />
          </Link>
        </div>
        
        <div className={`navbar-links ${mobileMenuOpen ? 'active' : ''}`}>
          <Link to="/" onClick={toggleMobileMenu}>Home</Link>
          <Link to="/pricing" onClick={toggleMobileMenu}>Pricing</Link>
          <Link to="/dashboard" onClick={toggleMobileMenu}>Dashboard</Link>
          
          {/* Admin links in mobile menu only */}
          {isLoggedIn && isAdmin && mobileMenuOpen && (
            <div className="mobile-admin-links">
              <div className="mobile-admin-header">Admin Menu</div>
              <Link to="/admin/system-settings" onClick={toggleMobileMenu}>System Settings</Link>
              <Link to="/admin/manage-admins" onClick={toggleMobileMenu}>Manage Admins</Link>
            </div>
          )}
        </div>

        <div className="navbar-auth">
          {/* Desktop Admin Menu - visible only in desktop */}
          {isLoggedIn && isAdmin && (
            <div className="admin-menu-container desktop-only">
              <button 
                className="admin-menu-btn" 
                onClick={handleAdminMenuClick}
              >
                Admin <span className="dropdown-arrow">▼</span>
              </button>
              {adminMenuOpen && (
                <div className="admin-dropdown">
                  <button onClick={() => navigateToAdminPage('/admin/system-settings')}>System Settings</button>
                  <button onClick={() => navigateToAdminPage('/admin/manage-admins')}>Manage Admins</button>
                </div>
              )}
            </div>
          )}
          
          <div className="user-menu-container">
            <button 
              className="sign-in-btn" 
              onClick={handleAuthClick}
            >
              {isLoggedIn ? `Hello ${user?.firstName || ''}` : 'Sign In'}
            </button>
            {isLoggedIn && userDropdownOpen && (
              <div className="user-dropdown">
                <button onClick={navigateToAccountSettings}>Account Settings</button>
                <button onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </div>

        <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          <div className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </nav>
      
      {/* Floating WhatsApp Button - Always circular with text inside when expanded */}
      <div className={`whatsapp-chat-widget ${scrolled ? 'expanded' : 'collapsed'}`} onClick={handleWhatsAppClick}>
        <div className="chat-content">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 24 24" 
            fill="white"
            className="whatsapp-inline-icon"
          >
            <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564c.173.087.289.129.332.202.043.073.043.423-.101.828z"/>
          </svg>
          {scrolled && (
            <span className="chat-tooltip">Chat with us</span>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar; 